// Pure decision logic for the creator "Mature content" self-label
// (minors-13plus plan B.14). React-free, so the parts where a wrong answer
// matters — the default rating, the envelope unwrap, and the one predicate that
// decides whether a badge mounts — are unit-assertable without a renderer.
//
// The server owns the AUDIENCE boundary: `yuga_wheel.Wheel.content_rating` plus
// the BE audience filter hide mature wheels from minors, unknown-age accounts
// and anonymous visitors. Nothing here filters anything; it only reads the
// rating the API already sent and decides how to render it.

export const CONTENT_RATING_GENERAL = "general";
export const CONTENT_RATING_MATURE = "mature";

// The exact creator-facing copy approved in the plan (B.14 + D). Kept in one
// place so the toggle and its tests can never drift apart.
export const MATURE_TOGGLE_LABEL = "Mature content";
export const MATURE_TOGGLE_COPY =
  "Mark this wheel as mature (18+). It is hidden from users under 18 and from visitors who are not signed in.";
export const MATURE_BADGE_LABEL = "Mature";

/**
 * Is this rating the mature one?
 *
 * DEFAULT IS GENERAL: only the exact string "mature" counts. Anything else —
 * null, undefined (an older wheel, a still-loading query, a backend that has
 * not shipped the field yet), "general", or an unrecognised value — reads as
 * general. Failing to general is the safe direction for the CREATOR-facing
 * toggle: it never silently claims a wheel is 18+ that the creator never
 * labelled. It is safe for the badge too, because the server, not this
 * function, is what keeps mature wheels away from minors.
 *
 * @param {unknown} rating
 * @returns {boolean}
 */
export function isMatureRating(rating) {
  return typeof rating === "string" && rating.toLowerCase() === CONTENT_RATING_MATURE;
}

/**
 * Turn the toggle's boolean into the value the wheel PUT/PATCH carries.
 * @param {boolean} checked
 * @returns {"general"|"mature"}
 */
export function ratingFromToggle(checked) {
  return checked ? CONTENT_RATING_MATURE : CONTENT_RATING_GENERAL;
}

/**
 * Extract `content_rating` from whatever shape a wheel payload arrives in.
 *
 * The app's axios interceptor returns the FULL response and the BE wraps the
 * wheel in a success envelope, so the wheel can be nested up to two levels
 * deep — and any level may already BE the wheel. Peel defensively, exactly as
 * readWheelCommentsEnabled() does for the sibling "Allow comments" control.
 *
 * @param {unknown} wheelResponse
 * @returns {"general"|"mature"}
 */
export function readWheelContentRating(wheelResponse) {
  const candidates = [
    wheelResponse,
    wheelResponse?.data,
    wheelResponse?.data?.data,
  ];
  for (const node of candidates) {
    if (node && typeof node === "object" && "content_rating" in node) {
      return isMatureRating(node.content_rating)
        ? CONTENT_RATING_MATURE
        : CONTENT_RATING_GENERAL;
    }
  }
  return CONTENT_RATING_GENERAL; // unknown => general (default)
}

/**
 * Should a wheel card mount the "Mature" badge?
 *
 * BOTH conditions are required and neither is a filter:
 *  - `flagEnabled` — REACT_APP_MINORS_POLICY_ENABLED. OFF keeps every card
 *    byte-identical to today.
 *  - the wheel's own `content_rating` is "mature".
 *
 * There is deliberately no viewer-age argument. The badge is an ADULT-only
 * sight purely because the backend audience filter never puts a mature wheel in
 * a minor's / unknown's / anonymous visitor's payload — so if a mature wheel is
 * on screen at all, the viewer is already an adult. Re-deciding that here would
 * be a second, weaker copy of a server-side boundary.
 *
 * @param {{flagEnabled?: boolean, contentRating?: unknown}} params
 * @returns {boolean}
 */
export function shouldShowMatureBadge({ flagEnabled, contentRating } = {}) {
  if (!flagEnabled) return false;
  return isMatureRating(contentRating);
}
